import type { EditablePost } from "../../api/client";

const publicationShapeContainsMedia = (value: unknown) => {
  if (!value || typeof value !== "object") return false;
  const messages = (value as { messages?: unknown }).messages;
  if (!Array.isArray(messages)) return false;
  return messages.some((candidate) => {
    if (!candidate || typeof candidate !== "object") return false;
    const message = candidate as { kind?: unknown; media?: unknown };
    return message.kind === "photo" || message.kind === "video" ||
      message.kind === "document" ||
      ((message.kind === "rich" || message.kind === "media_group") &&
        Array.isArray(message.media) && message.media.length > 0);
  });
};

const isRemovedMediaFailure = (post: EditablePost) =>
  post.liveSyncErrorCode === "MESSAGE_EDIT_FORBIDDEN" &&
  post.mediaItems.length === 0 &&
  publicationShapeContainsMedia(post.lastPublicationShape);

const failureReason = (post: EditablePost) => {
  const code = post.liveSyncErrorCode;
  if (code === "MESSAGE_NOT_FOUND") return "Последнее сообщение удалено или недоступно для бота.";
  if (isRemovedMediaFailure(post)) return "Фото удалено из настроек. Telegram не может превратить опубликованное сообщение с фото в текстовое без замены.";
  if (code === "MESSAGE_EDIT_FORBIDDEN") return "Telegram не разрешил изменить последнее сообщение. Проверьте права бота.";
  if (code === "INCOMPATIBLE_PUBLICATION_SHAPE") return "Новый вид нельзя безопасно применить к текущему сообщению без его замены.";
  if (code === "MEDIA_EDIT_UNSUPPORTED") return "Медиа текущего сообщения нельзя заменить в этой структуре.";
  if (code === "DELIVERY_UNCERTAIN") return "Telegram не подтвердил результат изменения. Проверьте сообщение перед повтором.";
  if (code === "RENDER_FAILED") return "Не удалось подготовить обновлённый вид текущего сообщения.";
  return "Текущее сообщение не удалось обновить безопасно.";
};

const isPermanentShapeFailure = (post: EditablePost) =>
  post.liveSyncErrorCode === "INCOMPATIBLE_PUBLICATION_SHAPE" ||
  post.liveSyncErrorCode === "MEDIA_EDIT_UNSUPPORTED" ||
  isRemovedMediaFailure(post);

export function PostLiveSyncStatus({
  post,
  retrying,
  replacing,
  onRetry,
  onReplace
}: {
  post: EditablePost;
  retrying: boolean;
  replacing: boolean;
  onRetry: () => void;
  onReplace: () => void;
}) {
  if (post.liveSyncState === "never") {
    return <section className="post-sync-status neutral"><p>Пост ещё не публиковался; изменения применятся при первой публикации</p></section>;
  }
  if (post.liveSyncState === "pending") {
    return <section className="post-sync-status pending" aria-live="polite"><p>Будущие публикации сохранены. Текущая обновляется</p></section>;
  }
  if (post.liveSyncState === "synced") {
    return <section className="post-sync-status success"><p>Текущая публикация синхронизирована</p></section>;
  }
  const permanentShapeFailure = isPermanentShapeFailure(post);
  return (
    <section className="post-sync-status failed" aria-live="polite">
      <p><strong>Будущие публикации сохранены. Текущая не синхронизирована</strong></p>
      <p>{failureReason(post)}</p>
      {permanentShapeFailure && (
        <p>Новый формат и медиа уже сохранены для будущих публикаций.</p>
      )}
      {permanentShapeFailure && (
        <button type="button" disabled={replacing} onClick={onReplace}>
          {replacing ? "Готовим замену…" : "Заменить опубликованное сообщение"}
        </button>
      )}
      {!permanentShapeFailure && post.liveSyncAppliedCount > 0 && (
        <p>Часть текущей публикации могла уже измениться; проверьте её перед повтором</p>
      )}
      {!permanentShapeFailure && (
        <button type="button" disabled={retrying} onClick={onRetry}>
          {retrying ? "Повторяем…" : "Повторить синхронизацию"}
        </button>
      )}
    </section>
  );
}
